import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./GroboChat.css";
import groboLogo from "../assets/images/grobo-logo.png";
import sendSound from "../assets/sound/send.mp3";
import typingSound from "../assets/sound/typing.mp3";

const API_URL = "https://apiv2.agrowtein.com/api/v1/bot/generalB";

const GroboChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [theme, setTheme] = useState("green");
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const notifDotRef = useRef(null);

  // Load saved data on mount
  useEffect(() => {
    const savedMessages = JSON.parse(localStorage.getItem("grobo_messages"));
    const savedSession = localStorage.getItem("grobo_sessionId");
    const savedTheme = localStorage.getItem("grobo_theme");

    if (savedMessages && savedMessages.length > 0) {
      setMessages(savedMessages);
    } else {
      const introMsg = {
        sender: "bot",
        text: "Hi...! I'm Grobo, your Smart Farming Assistant. Ask me anything about your crops, climate, or automation systems.",
        time: new Date().toLocaleTimeString(),
        intro: true, // Add this flag
      };
      setMessages([introMsg]);
    }

    if (savedSession) setSessionId(savedSession);
    if (savedTheme) setTheme(savedTheme);
  }, []);

  // Save messages, session and scroll chat on messages change
  useEffect(() => {
    // Save max 50 messages to localStorage
    const limitedMessages = messages.slice(-50);
    localStorage.setItem("grobo_messages", JSON.stringify(limitedMessages));
    if (sessionId) localStorage.setItem("grobo_sessionId", sessionId);

    // Scroll only if user near bottom
    if (chatContainerRef.current) {
      const el = chatContainerRef.current;
      const isNearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
      if (isNearBottom) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }

    // Show notification dot if minimized and new bot message arrived
    if (!isOpen && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.sender === "bot" && notifDotRef.current) {
        notifDotRef.current.classList.remove("hidden");
        // Optional: add a quick animation or flash class here
      }
    }
  }, [messages, sessionId, isOpen]);

  // Apply theme and save it
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("grobo_theme", theme);
  }, [theme]);

  // Autofocus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle sending message
  const handleSend = async (question = input) => {
    if (!question.trim()) return;

    const newMessage = {
      sender: "user",
      text: question,
      time: new Date().toLocaleTimeString(),
    };
    setMessages((prev) => [...prev, newMessage]);
    setInput("");
    setIsTyping(true);

    try {
      const response = await axios.post(API_URL, {
        question,
        session_id: sessionId || null,
      });

      const { answer, session_id } = response.data;
      const botMessage = {
        sender: "bot",
        text: answer,
        time: new Date().toLocaleTimeString(),
      };
      setMessages((prev) => [...prev, botMessage]);
      if (!sessionId && session_id) setSessionId(session_id);

      new Audio(sendSound).play();
    } catch (error) {
      console.error("Error talking to Grobo:", error);
      const fallbackMessage = {
        sender: "bot",
        text: "❗Oops! I'm having trouble right now. Please try again shortly.",
        time: new Date().toLocaleTimeString(),
      };
      setMessages((prev) => [...prev, fallbackMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Toggle chat window
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    if (isOpen && notifDotRef.current) {
      notifDotRef.current.classList.add("hidden");
    }
  };

  // Toggle theme between green and dark
  const toggleTheme = () => {
    setTheme((prev) => (prev === "green" ? "dark" : "green"));
  };

  // Clear chat history
  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem("grobo_messages");
    localStorage.removeItem("grobo_sessionId");
    setSessionId(null);
    // Optionally add intro message again
  };

  // Get last user message for minimized view
  const lastUserMessage =
    messages
      .slice()
      .reverse()
      .find((msg) => msg.sender === "user")?.text || "Grobo is here to help!";

  // Handle key down events in input
  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // If Shift+Enter, allow default (new line in input)
  };

  // Play typing sound when Grobo is typing
  useEffect(() => {
    let audio;
    if (isTyping) {
      audio = new Audio(typingSound);
      audio.loop = true;
      audio.volume = 0.3; // softer volume
      audio.play();
    }
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [isTyping]);

  return (
    <div className={`grobo-container ${theme}`}>
      {!isOpen && (
        <div
          className="grobo-minimized"
          onClick={toggleChat}
          style={{ position: "relative" }}
          role="button"
          tabIndex={0}
          aria-label="Open Grobo chat"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") toggleChat();
          }}
        >
          <img src={groboLogo} alt="Grobo" className="grobo-avatar-mini" />
          <div className="grobo-preview">{lastUserMessage}</div>
          <div
            id="grobo-notif-dot"
            className="notification-dot hidden"
            ref={notifDotRef}
            aria-label="New message notification"
          />
        </div>
      )}

      {isOpen && (
        <div className="grobo-chatbox glass" aria-live="polite">
          <div className="grobo-header">
            <img src={groboLogo} alt="Grobo" className="grobo-avatar" />
            <span>Grobo Assistant</span>
            <div className="grobo-header-buttons">
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                aria-label="Toggle theme"
              >
                🌓
              </button>
              <button
                className="clear-btn"
                onClick={clearChat}
                aria-label="Clear chat history"
                title="Clear Chat"
                style={{ marginRight: 8 }}
              >
                🗑️
              </button>
              <button
                className="close-btn"
                onClick={toggleChat}
                aria-label="Close chat"
                title="Close"
              >
                _
              </button>
            </div>
          </div>

          <div
            className="grobo-messages"
            ref={chatContainerRef}
            tabIndex={0}
            aria-label="Chat messages"
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`message ${msg.sender}${
                  msg.intro ? " intro-anim" : ""
                }`}
                role="article"
                aria-live={msg.sender === "bot" ? "polite" : undefined}
              >
                {msg.sender === "bot" && (
                  <img
                    src={groboLogo}
                    alt="Bot avatar"
                    className="chat-avatar"
                  />
                )}
                <div className="bubble">
                  {msg.text.split("\n").map((line, i) => (
                    <p key={i} style={{ margin: 0 }}>
                      {line}
                    </p>
                  ))}
                  <div className="timestamp">{msg.time}</div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="message bot" aria-live="polite">
                <img
                  src={groboLogo}
                  alt="Grobo typing"
                  className="chat-avatar"
                />
                <div
                  className="bubble typing-indicator"
                  aria-label="Grobo is typing"
                >
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="grobo-input">
            <input
              type="text"
              placeholder="Ask Grobo something..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              ref={inputRef}
            />
            <button onClick={() => handleSend()}>Send</button>
          </div>

          <div className="grobo-footer">Powered by Agrowtein Labs</div>
        </div>
      )}
    </div>
  );
};

export default GroboChat;
